<template>
  <Teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="modal-overlay" @click="cancelAvatarUpload">
      <div class="modal" @click.stop>
        <h3>修改头像</h3>
        <div class="avatar-upload">
          <input 
            type="file" 
            ref="avatarInputRef" 
            @change="handleAvatarChange" 
            accept="image/*"
            style="display: none;"
          />
          <button @click="triggerFileInput" class="upload-btn" :disabled="isUploading">
            {{ isUploading ? '上传中...' : '选择图片' }}
          </button>
          <div v-if="previewAvatar" class="avatar-preview">
            <img :src="previewAvatar" alt="头像预览图片" />
          </div>
          
          <!-- 上传进度条 -->
          <div v-if="isUploading" class="upload-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <div class="progress-text">{{ uploadProgress }}%</div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="cancelAvatarUpload" class="cancel-btn" :disabled="isUploading">取消</button>
          <button @click="uploadAvatar" :disabled="!previewAvatar || isUploading" class="confirm-btn">
            {{ isUploading ? '上传中...' : '确认上传' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import { ref, watch } from 'vue'
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { message } from '@/utils/ui-service'

export default {
  name: 'AvatarModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    username: {
      type: String,
      required: true
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const avatarInputRef = ref(null)
    const previewAvatar = ref('')
    const selectedAvatarFile = ref(null)
    const uploadProgress = ref(0)
    const isUploading = ref(false)

    watch(() => props.visible, (newVal) => {
      if (newVal) {
        previewAvatar.value = ''
        selectedAvatarFile.value = null
        uploadProgress.value = 0
        isUploading.value = false
      }
    })

    const triggerFileInput = () => {
      if (avatarInputRef.value) {
        avatarInputRef.value.click()
      }
    }

    const handleAvatarChange = (event) => {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          message.error('图片大小不能超过5MB')
          return
        }
        
        selectedAvatarFile.value = file
        const reader = new FileReader()
        reader.onload = (e) => {
          previewAvatar.value = e.target.result
        }
        reader.readAsDataURL(file)
      }
    }

    const cancelAvatarUpload = () => {
      if (isUploading.value) return
      emit('update:visible', false)
      previewAvatar.value = ''
      selectedAvatarFile.value = null
      uploadProgress.value = 0
    }

    const uploadAvatar = async () => {
      if (!selectedAvatarFile.value) return
      
      isUploading.value = true
      uploadProgress.value = 0
      
      try {
        const formData = new FormData()
        formData.append('avatar', selectedAvatarFile.value)
        formData.append('username', props.username)
        
        const response = await axios.post(`${serverUrl}/api/upload-avatar`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        })
        
        if (response.data.success) {
          isUploading.value = false
          message.success('头像更新成功！')
          emit('success', response.data.url)
          emit('update:visible', false)
          previewAvatar.value = ''
          selectedAvatarFile.value = null
          uploadProgress.value = 0
        } else {
            message.error(response.data.message || '上传头像失败，请稍后重试')
            isUploading.value = false
        }
      } catch (error) {
        console.error('上传头像失败:', error)
        message.error(error.response?.data?.error || '上传头像失败，请稍后重试')
        isUploading.value = false
      }
    }

    return {
      avatarInputRef,
      previewAvatar,
      isUploading,
      uploadProgress,
      triggerFileInput,
      handleAvatarChange,
      cancelAvatarUpload,
      uploadAvatar
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: scale(0.9);
}
.modal-enter-to .modal,
.modal-leave-from .modal {
  transform: scale(1);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal-elevated);
}

.modal {
  background: white;
  border-radius: 15px;
  padding: 30px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.modal h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 20px;
  text-align: center;
}

.avatar-upload {
  text-align: center;
  margin-bottom: 20px;
}

.upload-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.upload-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.upload-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.avatar-preview {
  margin-top: 15px;
  display: flex;
  justify-content: center;
}

.avatar-preview img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #f0f0f0;
}

.upload-progress {
  margin: 20px 0;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 500;
  color: #4CAF50;
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
}

.cancel-btn, .confirm-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 100px;
}

.cancel-btn {
  background: #f0f2f5;
  color: #606266;
}

.cancel-btn:hover:not(:disabled) {
  background: #e4e7ed;
}

.confirm-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.confirm-btn:disabled, .cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Dark Theme */
.dark-theme .modal {
  background: #2d2d2d;
  color: #e0e0e0;
}

.dark-theme .modal h3 {
  color: #e0e0e0;
}

.dark-theme .avatar-preview img {
  border-color: #3d3d3d;
}

.dark-theme .progress-bar {
  background-color: #3d3d3d;
}

.dark-theme .cancel-btn {
  background: #3d3d3d;
  color: #e0e0e0;
}

.dark-theme .cancel-btn:hover:not(:disabled) {
  background: #4d4d4d;
}
</style>
